import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';

import { ROUTES } from 'consts';
import { baseUrl } from './apis.const';

const XSRF_TOKEN = 'XSRF-TOKEN';
const CLEANCODE_REDIRECT_TOKEN = 'cleancode-redirect-token';

export const getCookie = (cName: string) => {
  const name = `${cName}=`;
  const cDecoded = decodeURIComponent(document.cookie);
  const cArr = cDecoded.split('; ');
  let res;
  cArr.forEach((val: string) => {
    if (val.indexOf(name) === 0) res = val.substring(name.length);
  });
  return res;
};

export const getCookies = () => {
  const xsrfToken = getCookie(XSRF_TOKEN);
  const cleancodeRedirectToken = getCookie(CLEANCODE_REDIRECT_TOKEN);

  return { cleancodeRedirectToken, xsrfToken };
};

const baseQuery = fetchBaseQuery({
  baseUrl,
  credentials: 'include',
  prepareHeaders(headers) {
    const { cleancodeRedirectToken, xsrfToken } = getCookies();

    headers.set('Accept', 'application/json');
    headers.set('X-Requested-With', 'XMLHttpRequest');

    if (cleancodeRedirectToken) {
      headers.set('cleancode-redirect-token', cleancodeRedirectToken);
    }
    if (xsrfToken) {
      headers.set('X-XSRF-TOKEN', xsrfToken);
    }

    return headers;
  },
});

const handleUnauthenticated = (api: { dispatch: (action: { type: string }) => void }) => {
  api.dispatch({ type: 'authSlice/logout' });

  try {
    const loginPath = `/${ROUTES.AUTH}/${ROUTES.AUTH_LOGIN}`;
    const isAlreadyOnLoginPage = window.location.pathname.startsWith(loginPath);

    if (!isAlreadyOnLoginPage) {
      const redirectUri = window.location.href;
      const searchParams = new URLSearchParams();

      if (redirectUri) {
        searchParams.set('redirect_uri', redirectUri);
      }

      window.location.href = `${loginPath}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    }
  } catch {
    // In non-browser environments (e.g. tests), window/location may be unavailable.
  }
};

// TODO: implement reset cookie
export const fetchbase: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    console.error(result.error);

    if (result.error.status === 401 || result.error.status === 419) {
      handleUnauthenticated(api);
    }
  } else {
    // Defense-in-depth: if the API returns a redirect that fetch followed silently
    // (e.g. a 302 to a login page from a misconfigured auth middleware), treat the
    // request as unauthenticated rather than letting the UI continue with a stale session.
    const response = result.meta?.response;
    if (response?.redirected && /\/login(\b|\/|\?|$)/.test(response.url)) {
      handleUnauthenticated(api);
    }
  }

  return result;
};

type TransformResponsePayload<T> = {
  payload: T;
};

export const transformResponse = ({ payload }: TransformResponsePayload<any>) => payload;

export const getQueryUrl = (payload: { url: string; queryObj?: { [key: string]: any } }) => {
  const { url, queryObj } = payload;

  if (!queryObj) return url;

  const keys = Object.keys(queryObj);

  let queryString = '';

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    if (queryObj[key]) {
      queryString += `${queryString ? '&' : '?'}${key}=${queryObj[key]}`;
    }
  }

  return `${url}${queryString}`;
};
